import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Check, ChevronDown } from 'lucide-react';
import ModalOverlay from './ModalOverlay';

/**
 * True when we should avoid native <select> (Android/iOS WebView system picker).
 */
export function prefersCustomSelectPicker() {
  if (Capacitor.isNativePlatform()) return true;
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 767px)').matches;
}

function usePrefersCustomSelectPicker() {
  const [custom, setCustom] = useState(() => prefersCustomSelectPicker());

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setCustom(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return custom;
}

/** Standard filter / form row trigger (touch-friendly on native). */
export const APP_SELECT_FILTER_TRIGGER =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-left min-h-[44px] flex items-center justify-between touch-manipulation';

/**
 * App-styled select. On native / narrow viewports opens a bottom sheet instead of the OS picker.
 */
export default function AppSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  className = '',
  triggerClassName = '',
  id: idProp,
  'aria-label': ariaLabel,
  name,
  sheetTitle,
  forceCustomPicker,
  forceNativeSelect,
}) {
  const autoId = useId();
  const id = idProp || autoId;
  const listId = `${id}-list`;
  const prefersCustom = usePrefersCustomSelectPicker();
  const useCustom =
    forceNativeSelect === true
      ? false
      : forceCustomPicker === true || prefersCustom;

  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );

  const displayLabel = selected?.label ?? placeholder;

  const commit = (nextValue) => {
    if (disabled) return;
    onChange?.({ target: { value: nextValue, name } });
    setOpen(false);
    triggerRef.current?.focus();
  };

  if (!useCustom) {
    return (
      <select
        id={id}
        name={name}
        aria-label={ariaLabel}
        disabled={disabled}
        value={value ?? ''}
        onChange={onChange}
        className={className || triggerClassName}
      >
        {placeholder && !options.some((o) => o.value === '') && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((o) => (
          <option key={String(o.value)} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  const triggerClasses =
    triggerClassName ||
    className ||
    'w-full min-h-[44px] px-4 py-3 text-base text-left border border-slate-200 rounded-xl bg-white shadow-sm text-slate-800 flex items-center justify-between gap-2 touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:opacity-60';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => !disabled && setOpen(true)}
        className={triggerClasses}
      >
        <span className="truncate flex-1">{displayLabel}</span>
        <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
      </button>

      <ModalOverlay open={open} onClose={() => setOpen(false)} align="bottom-sheet" zIndexClass="z-[200]">
        <div
          id={listId}
          role="listbox"
          aria-label={ariaLabel || sheetTitle || 'Choose an option'}
          className="w-full max-h-[min(75dvh,calc(100dvh-var(--safe-area-inset-top,env(safe-area-inset-top,0px))-var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))))] overflow-hidden rounded-t-2xl bg-white shadow-xl border-t border-slate-200"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-900 truncate">
              {sheetTitle || ariaLabel || 'Select'}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-blue-600 py-2 px-1 touch-manipulation min-h-[44px]"
            >
              Done
            </button>
          </div>
          <ul className="overflow-y-auto max-h-[min(65dvh,calc(100dvh-var(--safe-area-inset-top,env(safe-area-inset-top,0px))-var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))-4rem))] py-1">
            {options.map((o) => {
              const isSelected = String(o.value) === String(value);
              return (
                <li key={String(o.value)} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => commit(o.value)}
                    className={`w-full text-left px-4 py-3.5 min-h-[48px] flex items-center justify-between gap-3 text-base touch-manipulation border-b border-slate-50 last:border-0 ${
                      isSelected ? 'bg-blue-50 text-blue-800 font-medium' : 'text-slate-800 active:bg-slate-50'
                    }`}
                  >
                    <span className="truncate">{o.label}</span>
                    {isSelected && <Check className="h-5 w-5 shrink-0 text-blue-600" aria-hidden />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </ModalOverlay>
    </>
  );
}
